// Libraries
import React from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

// Utilities
import { copyToClipboard, populatePath, translateDate } from 'common/utils/helpers';

// Enums
import { ResultsSummaryType } from 'engagementReport/enums/propTypes';
import ProjectStatus from 'project/enums/ProjectStatus';

// Routes
import Routes from 'routes';

// Hooks
import { useIsFiltered } from '../hooks';

const ResultsSummarySidebar = ({
  resultsSummary,
  projectStatus,
  projectHubEnabled,
  multiPages,
  onDisplayToggle,
}) => {
  const params = useParams();
  const { t } = useTranslation();
  const { engagement, filterOptions } = resultsSummary;
  const { project } = engagement;
  const showProjectHubLink =
    projectHubEnabled &&
    (projectStatus === ProjectStatus.ACTIVE || projectStatus === ProjectStatus.FINISHED);
  const filtered = useIsFiltered(filterOptions);
  const asterisk = filtered ? '*' : '';
  const participantsText = `${resultsSummary.engagementSummary.started} ${t(
    'engagement_report.participants'
  )} ${asterisk}`;
  const { startTime } = resultsSummary.engagementSummary;
  const { endTime } = resultsSummary.engagementSummary;

  const copyLinkToClipboard = () => copyToClipboard(window.location.href);

  return (
    <div className="results-summary-sidebar">
      <div className="results-summary-sidebar__actions">
        <div className="results-summary-sidebar__actions__display-toggle">
          <i className={classNames('fas', { 'fa-file': multiPages, 'fa-copy': !multiPages })} />
          <button type="button" onClick={onDisplayToggle}>
            {multiPages ? 'Display as One Single Page' : 'Display as Separate Pages'}
          </button>
        </div>
        <div className="results-summary-sidebar__actions__separator" />
        <div className="results-summary-sidebar__actions__share">
          <i className="fas fa-share-alt" />{' '}
          <button type="button" onClick={copyLinkToClipboard}>
            Share
          </button>
        </div>
      </div>
      <div className="results-summary-sidebar__content">
        <div className="title is-4 is-primary-color results-summary-sidebar__title">
          {engagement.title}
        </div>
        <div>
          {`${translateDate(startTime, t)} ${
            Boolean(endTime) && `to ${translateDate(endTime, t)}`
          }`}
        </div>
        <div>{participantsText}</div>
        {showProjectHubLink && (
          <div className="results-summary-sidebar__project-link">
            Part Of{' '}
            <a
              href={populatePath(Routes.PROJECT, params)}
              className="is-secondary-color results-summary-sidebar__link"
            >
              {project.title}
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

ResultsSummarySidebar.propTypes = {
  resultsSummary: ResultsSummaryType.isRequired,
  projectStatus: PropTypes.string,
  projectHubEnabled: PropTypes.bool.isRequired,
  multiPages: PropTypes.bool,
  onDisplayToggle: PropTypes.func,
};

ResultsSummarySidebar.defaultProps = {
  projectStatus: '',
  multiPages: false,
  onDisplayToggle: () => {},
};

export default ResultsSummarySidebar;
